import {
  getEnvironment,
  getSfdcEnv,
  UserProfile,
  getSplatUserType,
  INSIDE_SALES,
  SALES_ONLY_PARTNER,
  OTHER,
  FUSION_CLOSER,
  FIELD_SALES,
  DIRECT_TO_HOME,
  CHANNEL_PARTNER,
} from '@sunrun/sales-experience-shared';
import { useMemo } from 'react';
import styled from '@emotion/styled/macro';
import { SvgIcon, SvgNames, tokens } from '@sunrun/experience-ui-components';
import { useFeatureFlagString } from '@sunrun/sales-experience-shared/react-feature-flags';
import {
  navigateToCustomerProfile,
  navigateToProspect,
  viewInSalesforce,
  copyCustomerInformation,
  viewOfferPresentation,
  getPartnerBaseUrl,
  getSalesforceBaseUrl,
} from 'helpers/listViewHelpers';
import { useHybridToken } from 'providers/Auth';
import { useUserProfile } from 'providers/UserProfile';
import { Prospect } from 'types/prospect';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import { useCap } from 'providers/Cap';

type ListViewMenuOptionsProps = {
  Child: React.ElementType;
  prospect: Prospect;
};

const ListViewMenuOptions = ({ Child, prospect }: ListViewMenuOptionsProps) => {
  const { userProfile } = useUserProfile();
  const { hybridToken, hybridTokenType } = useHybridToken();
  const env = getEnvironment();

  const configuredPartnerSfdcUrl = useFeatureFlagString(
    FEATURE_FLAGS.SFDC_PARTNER_URL,
  );
  const configuredSfdcUrl = useFeatureFlagString(FEATURE_FLAGS.SFDC_URL);
  const SFDCEnv = getSfdcEnv(userProfile as UserProfile);
  const { prospectId, opportunityId, leadId, leadStage } = prospect as Prospect;
  const isD2HUser = SFDCEnv === 'D2HCommunity';
  const opptyIsStage7Through10: boolean = useMemo(() => {
    return ['7', '8', '9', '10'].some((stage) => leadStage?.includes(stage));
  }, [leadStage]);
  const { projectId, isLoading: isLoadingCap } = useCap(
    prospectId,
    opptyIsStage7Through10,
  );

  const partnerBaseUrl = getPartnerBaseUrl(env, configuredPartnerSfdcUrl);
  const salesforceBaseUrl = getSalesforceBaseUrl(env, configuredSfdcUrl);
  const d2hBaseUrl = `${partnerBaseUrl}/d2hpartnercommunity`;
  const communityBaseUrl = `${partnerBaseUrl}/partners`;

  const salesforceUrl = useMemo(() => {
    const recordId = opportunityId ?? leadId ?? '';

    switch (SFDCEnv) {
      case 'D2HCommunity':
        return opportunityId
          ? `${d2hBaseUrl}/PartnerOpportunityCreate?id=${opportunityId}`
          : `${d2hBaseUrl}/apex/customizeLeadDetailPage?Id=${leadId}`;
      case 'Community':
        return opportunityId
          ? `${communityBaseUrl}/s/partnerOpp?id=${opportunityId}`
          : `${communityBaseUrl}/apex/customizeLeadDetailPage?Id=${leadId}`;
      default:
        return `${salesforceBaseUrl}/${recordId}`;
    }
  }, [
    SFDCEnv,
    d2hBaseUrl,
    communityBaseUrl,
    salesforceBaseUrl,
    opportunityId,
    leadId,
  ]);

  const viewProjectUrl = useMemo(() => {
    if (!projectId) return '';
    switch (SFDCEnv) {
      case 'D2HCommunity':
        return `${d2hBaseUrl}/WFProjectRedirectPage?id=${projectId}`;
      case 'SFDC':
        return `${salesforceBaseUrl}/${projectId}`;
      default:
        return '';
    }
  }, [SFDCEnv, d2hBaseUrl, salesforceBaseUrl, projectId]);

  const splatBaseUrl =
    env === 'production'
      ? 'https://go.sunrun.com'
      : 'https://majstg-go.sunrun.com';
  const splatUserType = getSplatUserType(userProfile as UserProfile);

  const menuItems = [
    {
      label: 'View Prospect',
      icon: SvgNames.AccountPlus,
      onClick: () => navigateToProspect(prospectId),
      checks: [],
    },
    {
      label: 'View Opportunity',
      icon: SvgNames.ArrowSquareOutSvg,
      onClick: () => viewInSalesforce(salesforceUrl),
      checks: [isD2HUser && (!!leadId || !!opportunityId) && !!salesforceUrl],
    },
    {
      label: (
        <FlexLoadingWrapper>
          <span
            style={{
              color: isLoadingCap ? tokens.TINTS_OFF_WHITE_30 : tokens.BLACK,
            }}>
            View Project
          </span>
          {isLoadingCap ? (
            <SvgIcon
              width={16}
              height={16}
              color={tokens.BRAND_OFF_GREY}
              name={SvgNames.Loading}
            />
          ) : null}
        </FlexLoadingWrapper>
      ),
      icon: SvgNames.ArrowSquareOutSvg,
      onClick: () => {
        if (isLoadingCap || !viewProjectUrl) return;
        viewInSalesforce(viewProjectUrl);
      },
      checks: [
        isD2HUser,
        isLoadingCap || !!projectId,
        opptyIsStage7Through10,
        !!opportunityId,
      ],
    },
    {
      label: 'View Customer Profile',
      icon: SvgNames.Account,
      onClick: () => navigateToCustomerProfile(prospectId),
      checks: [prospect.opportunityId ? true : false],
    },
    {
      label: 'View Offer Presentation',
      icon: SvgNames.Home,
      onClick: () =>
        viewOfferPresentation(
          userProfile as UserProfile,
          prospectId,
          splatBaseUrl,
          hybridToken,
          hybridTokenType,
        ),
      checks: [
        [
          CHANNEL_PARTNER,
          DIRECT_TO_HOME,
          FIELD_SALES,
          FUSION_CLOSER,
          INSIDE_SALES,
          OTHER,
          SALES_ONLY_PARTNER,
        ].includes(splatUserType),
        prospect.opportunityId ? true : false,
      ],
    },
    {
      label: 'View in Salesforce',
      icon: SvgNames.ArrowSquareOutSvg,
      onClick: () => viewInSalesforce(salesforceUrl),
      checks: [SFDCEnv === 'SFDC' ? true : false],
    },
    {
      label: 'View Opportunity in Partner Portal',
      icon: SvgNames.ArrowSquareOutSvg,
      onClick: () => salesforceUrl && viewInSalesforce(salesforceUrl),
      checks: [
        SFDCEnv === 'Community',
        (!!opportunityId || !!leadId) && !!salesforceUrl,
      ],
    },
    {
      label: 'Copy Customer Information',
      icon: SvgNames.Copy,
      onClick: () => copyCustomerInformation(prospect),
      checks: [],
    },
  ];

  return (
    <>
      {menuItems
        .filter(
          ({ checks }) => !checks.length || checks.every((check) => check),
        )
        .map(({ label, icon, onClick }) => (
          <Child key={label} onClick={onClick} label={label} icon={icon} />
        ))}
    </>
  );
};

const FlexLoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export { ListViewMenuOptions };
